import {useMaterialStorageDataQuery} from '@domain/material-storage'
import {useCurrentScheduleQuery, useStockDevelopmentQuery} from '@domain/schedule'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {useResizableChartWidth} from '@widgets/common-d3-chart'
import {
  getListOfDays,
  MomentRange,
  usePlanningChartStartEnd
} from '@widgets/planning-schedule-chart'
import {
  StockDevelopmentChartConfigProvider,
  STOCK_DEVELOPMENT_CHART_DEFAULT_HEIGHT,
  useStockDevelopmentChartConfigData
} from '@widgets/stock-card'

import {useSelectedOptimizerPlan} from '../../../providers'
import {useSelectedMaterialId} from '../SelectedMaterialIdProvider'

import {OptimizerStockLevelMaterialDetailChartContent} from './OptimizerStockLevelMaterialDetailChartContent'

export const OptimizerStockLevelMaterialDetailChart = ({plantCode}: {plantCode: string}) => {
  const [chartRef, width] = useResizableChartWidth()

  const {materialId} = useSelectedMaterialId()
  if (materialId === undefined) {
    throw new Error('Must have selected material')
  }

  const timezoneId = useUpmPlantTimezone()
  const {startOfChart, endOfChart, startOfPlan, endOfPlan} = usePlanningChartStartEnd()
  const chartRange = [startOfChart, endOfChart] as MomentRange
  const planRange = [startOfPlan, endOfPlan] as MomentRange

  const {selectedInTableScheduleId} = useSelectedOptimizerPlan()

  const {data: stockDevelopment} = useStockDevelopmentQuery({
    materialIds: [materialId],
    scheduleId: selectedInTableScheduleId,
    timeframe: planRange
  })
  const {data: manualSchedule} = useCurrentScheduleQuery({plantId: plantCode, range: chartRange})

  const isOptimizedPlanSelected = selectedInTableScheduleId !== undefined

  const {data: stockDevelopmentProductionPlan} = useStockDevelopmentQuery({
    materialIds: isOptimizedPlanSelected && manualSchedule ? [materialId] : [],
    scheduleId: manualSchedule?.scheduleId,
    timeframe: planRange
  })

  const {data: storageData} = useMaterialStorageDataQuery({materialId})

  const listOfDays = getListOfDays({start: chartRange[0], end: chartRange[1], additionalDays: 1})

  const configData = useStockDevelopmentChartConfigData({
    stockData: stockDevelopment
      ? stockDevelopment[materialId]
      : {
          stockLevel: {actual: [], forecast: [], combined: []},
          stockLevelHourly: {}
        },
    stockDataSecondary: stockDevelopmentProductionPlan?.[materialId],
    listOfDays: listOfDays,
    timezoneId,
    minTargetLevels: storageData
      ? {
          week: storageData.minTargetLevel,
          weekend: storageData.minTargetLevelWeekend
        }
      : undefined,
    storageCapacity: storageData?.storageCapacity,
    height: STOCK_DEVELOPMENT_CHART_DEFAULT_HEIGHT,
    width
  })

  return (
    <StockDevelopmentChartConfigProvider {...configData}>
      <OptimizerStockLevelMaterialDetailChartContent
        plantCode={plantCode}
        chartRef={chartRef}
        width={width}
        materialId={materialId}
      />
    </StockDevelopmentChartConfigProvider>
  )
}
